import { EditorState } from '@tiptap/pm/state'
import { Node as PMNode } from '@tiptap/pm/model'
import { findInstalledCommand, TInstalledCommand } from '@/atoms/slash-commands'
import { getActiveArgs, setActiveArgIndex } from './suggestionState'

type EditorLike = {
  chain: () => {
    focus: () => {
      insertContentAt: (range: { from: number; to: number }, content: string) => { run: () => void }
    }
  }
}

export function parseArgTexts(text: string): string[] {
  const trimmed = text.trim()
  return trimmed.length === 0 ? [] : trimmed.split(/\s+/)
}

/**
 * A slash-command invocation looks like:
 *   [/cmd @nostr:npub... slashCommand node][text: " arg1 arg2 …"]
 * all inside one paragraph. Returns the positions of those parts when the
 * cursor sits inside (or at the start of) the args region.
 */
export function findInvocation($pos: { pos: number; parentOffset: number; parent: any }): {
  slashPos: number
  argsStart: number
  argsEnd: number
} | null {
  if (!$pos.parent.isTextblock) return null
  const parentStart = $pos.pos - $pos.parentOffset
  const children = $pos.parent.content.content as readonly PMNode[]

  // find the last slashCommand node before the cursor
  let slashPos: number | null = null
  let slashIndex = -1
  let pos = parentStart
  for (let i = 0; i < children.length; i++) {
    const child = children[i]
    if (pos >= $pos.pos) break
    const end = pos + child.nodeSize
    if (child.type.name === 'slashCommand' && end <= $pos.pos) {
      slashPos = pos
      slashIndex = i
    }
    pos = end
  }
  if (slashPos == null || slashIndex < 0) return null

  const argsStart = slashPos + (children[slashIndex].nodeSize as number)
  if (argsStart > $pos.pos) return null

  return {
    slashPos,
    argsStart,
    argsEnd: $pos.pos
  }
}

/**
 * How many complete words the user has typed in the args region so far.
 */
export function countFilledArgs(
  doc: EditorState['doc'],
  invocation: { argsStart: number; argsEnd: number }
): number {
  const raw = doc.textBetween(invocation.argsStart, invocation.argsEnd, '\n', ' ')
  return parseArgTexts(raw).length
}

/**
 * Custom find() for @tiptap/suggestion: reports a match at the cursor while
 * it sits in the args region of an invoked command. Runs inside plugin
 * apply() (including for history remaps), so it must stay pure.
 */
export function findArgsMatch({
  $position
}: {
  $position: { pos: number; doc: EditorState['doc']; parentOffset: number; parent: any }
}): { range: { from: number; to: number }; query: string; text: string } | null {
  const active = getActiveArgs()
  if (!active) return null

  const invocation = findInvocation($position)
  if (!invocation) return null

  const definition = findInstalledCommand(active.pubkey, active.d)
  if (!definition || definition.args.length === 0) return null

  const filled = countFilledArgs($position.doc, invocation)
  if (filled >= definition.args.length) return null

  const index = Math.min(filled, definition.args.length - 1)
  setActiveArgIndex(index)

  const from = Math.max(invocation.argsStart, Math.min($position.pos, invocation.argsEnd))
  // extend the match to the end of the partially-typed word so completions
  // replace the fragment instead of concatenating to it
  const tail = $position.doc.textBetween($position.pos, invocation.argsEnd, '\n', ' ')
  const to = $position.pos + (tail.match(/^\S*/)?.[0].length ?? 0)

  return {
    range: { from, to },
    query: $position.doc.textBetween(from, $position.pos, '\n', ' '),
    text: ''
  }
}

/**
 * Insert an argument value at the suggestion range (plus a trailing space).
 */
export function insertArgValue(
  editor: EditorLike,
  range: { from: number; to: number },
  value: string
) {
  editor
    .chain()
    .focus()
    .insertContentAt(range, value + ' ')
    .run()
}

/**
 * Resolve the definition of the currently active command, if any.
 */
export function resolveActiveDefinition(): TInstalledCommand | undefined {
  const active = getActiveArgs()
  if (!active) return undefined
  return findInstalledCommand(active.pubkey, active.d)
}
